import { FEATURES, HOSTED_DOMAIN } from '../data/content';
import { useReveal } from '../hooks/useReveal';
import { BrowserWindow } from './shared/BrowserWindow';
import { CommentBubble } from './shared/CommentBubble';
import './FeatureTouts.scss';

/**
 * Alternating feature rows, Design-2 flavored. Each row pairs a big
 * lowercase headline + kicker + body on one side with a stylised
 * demo card on the other (the BrowserWindow + CommentBubble combo
 * used everywhere else on the page).
 *
 * Same 4 features as v1 — anchor / live / versions / hosted — just
 * presented with the cleaner, more spacious visual treatment.
 */
export function FeatureTouts() {
	return (
		<section class="m-feat" id="features">
			<div class="m-feat__container">
				{FEATURES.map((f, i) => (
					<FeatureRow key={f.kicker} feature={f} reverse={i % 2 === 1} index={i} />
				))}
			</div>
		</section>
	);
}

function FeatureRow(props: {
	feature: (typeof FEATURES)[number];
	reverse: boolean;
	index: number;
}) {
	const ref = useReveal<HTMLDivElement>();
	const { feature, reverse, index } = props;
	return (
		<div class={`m-feat__row ${reverse ? 'm-feat__row--reverse' : ''}`} ref={ref}>
			<div class="m-feat__copy">
				<span class="m-feat__kicker">{feature.kicker}</span>
				<h3 class="m-feat__title">{feature.title}</h3>
				<p class="m-feat__body">{feature.body}</p>
				<ul class="m-feat__list">
					{feature.highlights.map((h) => (
						<li key={h} class="m-feat__list-item">
							<span class="m-feat__check" aria-hidden="true" />
							<span>{h}</span>
						</li>
					))}
				</ul>
			</div>
			<div class="m-feat__visual">
				<FeatureCard index={index} />
			</div>
		</div>
	);
}

/**
 * Demo card per feature. Same BrowserWindow chrome as the hero so
 * everything reads as the same product surface; the inner content
 * + the floating CommentBubble specialise per feature.
 */
function FeatureCard({ index }: { index: number }) {
	if (index === 0) {
		// Anchoring — paragraph with a highlighted span + bubble attached.
		return (
			<BrowserWindow url="docs.acme.com/api/rate-limit" size="md">
				<div class="m-feat-card">
					<div class="m-feat-card__line" />
					<div class="m-feat-card__line m-feat-card__line--hl">
						<span class="m-feat-card__line-text">
							60 requests per minute per API key
						</span>
					</div>
					<div class="m-feat-card__line" />
					<div class="m-feat-card__line m-feat-card__line--short" />
					<CommentBubble
						author="Nora"
						body="Does this still hold after the v0.4 schema change?"
						hue="violet"
						timestamp="4m"
						style={{ top: '32%', right: '-16px' }}
					/>
				</div>
			</BrowserWindow>
		);
	}
	if (index === 1) {
		// Live presence — avatars + activity stream + bubble.
		return (
			<BrowserWindow url="docs.acme.com/quickstart" size="md">
				<div class="m-feat-card">
					<div class="m-feat-card__presence">
						<span class="m-feat-card__avatar m-feat-card__avatar--a">n</span>
						<span class="m-feat-card__avatar m-feat-card__avatar--b">j</span>
						<span class="m-feat-card__avatar m-feat-card__avatar--c">s</span>
						<span class="m-feat-card__avatar m-feat-card__avatar--more">+3</span>
						<span class="m-feat-card__presence-label">
							<span class="m-feat-card__dot" />6 viewing
						</span>
					</div>
					<div class="m-feat-card__stream">
						<div class="m-feat-card__stream-line">
							<span class="m-feat-card__time">just now</span>
							<span>
								<strong>jordan</strong> replied to a thread
							</span>
						</div>
						<div class="m-feat-card__stream-line">
							<span class="m-feat-card__time">8s</span>
							<span>
								<strong>sam</strong> resolved a comment
							</span>
						</div>
						<div class="m-feat-card__stream-line m-feat-card__stream-line--faded">
							<span class="m-feat-card__time">32s</span>
							<span>
								<strong>nora</strong> reacted with 👀
							</span>
						</div>
					</div>
					<CommentBubble
						author="Jordan"
						body="Just shipped — confirms with sam's run"
						hue="teal"
						timestamp="now"
						style={{ bottom: '-14px', left: '8%' }}
					/>
				</div>
			</BrowserWindow>
		);
	}
	if (index === 2) {
		// Versions — chip stack + bubble.
		return (
			<BrowserWindow url="docs.acme.com/changes" size="md">
				<div class="m-feat-card m-feat-card--versions">
					<div class="m-feat-card__spine" />
					<div class="m-feat-card__version">
						<span class="m-feat-card__sha">a1b2c3d</span>
						<span class="m-feat-card__msg">Update rate-limit copy in /api ref</span>
						<span class="m-feat-card__time">2m</span>
					</div>
					<div class="m-feat-card__version m-feat-card__version--hl">
						<span class="m-feat-card__sha">f4e5d6a</span>
						<span class="m-feat-card__msg">Add Python example next to curl</span>
						<span class="m-feat-card__time">14m</span>
						<span class="m-feat-card__badge">3 comments re-anchored</span>
					</div>
					<div class="m-feat-card__version">
						<span class="m-feat-card__sha">7c8d9e0</span>
						<span class="m-feat-card__msg">Fix typo in install steps</span>
						<span class="m-feat-card__time">2h</span>
					</div>
					<CommentBubble
						author="Sam"
						body="Nice — re-anchored cleanly across the rename."
						hue="amber"
						timestamp="14m"
						style={{ top: '38%', right: '-12px' }}
					/>
				</div>
			</BrowserWindow>
		);
	}
	// index === 3 — snippet → hosted.
	return (
		<BrowserWindow url={`${HOSTED_DOMAIN}/setup`} size="md">
			<div class="m-feat-card m-feat-card--toggle">
				<div class="m-feat-card__toggle-row">
					<div class="m-feat-card__toggle-card">
						<span class="m-feat-card__toggle-h">Add a snippet</span>
						<code class="m-feat-card__toggle-code">&lt;script src="…" /&gt;</code>
						<span class="m-feat-card__toggle-meta">one line, any page</span>
					</div>
					<div class="m-feat-card__toggle-or">→</div>
					<div class="m-feat-card__toggle-card m-feat-card__toggle-card--hl">
						<span class="m-feat-card__toggle-h">Hosted</span>
						<code class="m-feat-card__toggle-code">{HOSTED_DOMAIN}</code>
						<span class="m-feat-card__toggle-meta">Free · 1 project · 5 members</span>
					</div>
				</div>
				<div class="m-feat-card__toggle-foot">
					same code · same product · same license
				</div>
			</div>
		</BrowserWindow>
	);
}
